import { useCallback } from "react";
import { useIntl } from "react-intl";

// Intl.DisplayNames/ListFormat construction is non-trivial work (it loads locale data) and the
// active UI locale rarely changes within a session, yet every row that named a language or
// country previously built a brand-new instance on every call. Cache one instance per
// (locale, kind) instead — shared across every hook instance and every render.
const displayNamesCache = new Map<string, Intl.DisplayNames>();
function getDisplayNames(locale: string, type: "language" | "region"): Intl.DisplayNames {
  const key = `${locale}:${type}`;
  let dn = displayNamesCache.get(key);
  if (!dn) {
    dn = new Intl.DisplayNames([locale], { type });
    displayNamesCache.set(key, dn);
  }
  return dn;
}

const listFormatCache = new Map<string, Intl.ListFormat>();
function getListFormat(locale: string): Intl.ListFormat {
  let lf = listFormatCache.get(locale);
  if (!lf) {
    lf = new Intl.ListFormat(locale, { style: "long", type: "conjunction" });
    listFormatCache.set(locale, lf);
  }
  return lf;
}

/**
 * Human-readable language name for a locale code (e.g. "es" → "Español" when the UI is
 * in Spanish, "Spanish" when it's in English), resolved via the runtime's own locale
 * database instead of a fixed lookup table. The set of translations a promotion carries
 * is dynamic — it mirrors whatever locales the engine has active at the time — so a
 * hardcoded map would silently fall back to the raw code for anything not in the list.
 */
export function useLocaleName(): (locale?: string) => string {
  const intl = useIntl();
  return useCallback(
    (locale?: string) => {
      if (!locale) return "";
      const code = locale.toLowerCase().slice(0, 2);
      try {
        const name = getDisplayNames(intl.locale, "language").of(code);
        return name ? name.charAt(0).toUpperCase() + name.slice(1) : locale.toUpperCase();
      } catch {
        return locale.toUpperCase();
      }
    },
    [intl.locale]
  );
}

// Join a list of names as "A, B and C", localized to the current UI language instead of
// a hardcoded conjunction (the previous version always used the Spanish "y").
export function useJoinAnd(): (items: string[]) => string {
  const intl = useIntl();
  return useCallback((items: string[]) => getListFormat(intl.locale).format(items), [intl.locale]);
}

// Human-readable country name for an ISO alpha-2 code (e.g. "ES" → "España" when the UI is in
// Spanish), same pattern as useLocaleName above but for regions. `Intl.DisplayNames.of` throws
// for anything that isn't a valid region subtag — the backend's `changes.geolocation` diff
// (update flow) always sends real ISO codes, but the create card's raw includeCountries/
// excludeCountries can still carry whatever free-text country name the model used (there is no
// server-side resolution step before that approval card renders), so this falls back to the
// input unchanged rather than crashing, keeping ONE code path safe for both flows.
export function useCountryName(): (code: string) => string {
  const intl = useIntl();
  return useCallback(
    (code: string) => {
      if (!code) return code;
      try {
        const name = getDisplayNames(intl.locale, "region").of(code.toUpperCase());
        return name ?? code;
      } catch {
        return code;
      }
    },
    [intl.locale]
  );
}
